import type { QueryClient } from "@tanstack/react-query";
import { isRedirect, redirect } from "@tanstack/react-router";

export type AuthRouterContext = {
  queryClient: QueryClient;
}

function getErrorStatus(err: unknown): number | undefined {
  if (err && typeof err === "object" && "status" in err) {
    const s = (err as { status: unknown }).status;
    return typeof s === "number" ? s : undefined;
  }
  return undefined;
}

/** Use router `location.href` (path + search + hash, no origin). */
export function redirectSearchFromLocation(location: { href: string }) {
  return location.href;
}

export function createRequireAuthBeforeLoad(opts: {
  loginPath: string;
  getSessionQueryOptions: () => Parameters<QueryClient["ensureQueryData"]>[0];
}) {
  return async ({
    context,
    location,
  }: {
    context: unknown;
    location: { href: string };
  }) => {
    const queryClient = (context as Partial<AuthRouterContext>).queryClient;
    if (!queryClient) {
      throw new Error("Router context missing queryClient");
    }

    const redirectToLogin = () =>
      redirect({
        to: opts.loginPath,
        search: {
          redirect: redirectSearchFromLocation(location),
        },
      });

    try {
      const user = await queryClient.ensureQueryData(opts.getSessionQueryOptions());
      if (user === null || user === undefined) {
        throw redirectToLogin();
      }
    } catch (e) {
      if (isRedirect(e)) throw e;
      const status = getErrorStatus(e);
      if (status === 401 || status === 403) {
        throw redirectToLogin();
      }
      // Fetch/network or SDK errors often have no `status` — avoid an empty route error screen.
      if (status == null) {
        throw redirectToLogin();
      }
      throw e;
    }
  };
}
